import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatDate } from '../../lib/format'
import { OUTCOME_CHOICES, OUTCOME_WORDS, outcomeWords, statusWords, viaWords } from '../../lib/outbox'
import type { OutboundView, Outcome, SendGrantView } from '../../types/outbox'

// The Outbox page's pieces, presentation only. Copy is a rep's: "reply", "meeting booked", "no answer yet".
// A sent row carries what the nightly read decided and a control to say otherwise — the person's word is
// final. The grant panel says in one sentence whether a send from the mailbox is possible and why not.

/** One message on the list. */
export function OutboxRow({ view, onOutcome, correct, busy = false }: { view: OutboundView; onOutcome?: (o: Outcome) => void; correct?: ReactNode; busy?: boolean }) {
  const { row } = view
  return (
    <li className={`outbox-row status-${row.status}`}>
      <div className="outbox-row-head">
        <div>
          <strong>{row.subject || '(no subject)'}</strong>
          <span className="muted small"> · to {row.toName}</span>
        </div>
        <span className="muted small">
          {statusWords(row.status)}
          {row.status === 'sent' && row.sentAt ? ` ${formatDate(row.sentAt)} · ${viaWords(row.sentVia)}` : ''}
        </span>
      </div>
      <p className="outbox-row-tactic">
        {row.tacticId && view.tacticName
          ? <span className="tactic-chip" title={row.tacticWhy ?? undefined}>Tactic: {view.tacticName}</span>
          : <span className="muted small">No play from the playbook fits this one.</span>}
        {row.wasEdited && <span className="muted small"> · your words</span>}
        {correct}
      </p>
      {row.status === 'sent' && (
        <p className="outbox-row-outcome small">
          <span className={`outcome ${row.outcome ?? 'waiting'}`}>{outcomeWords(row.outcome)}</span>
          {row.outcome && <span className="muted"> · {row.outcomeSource === 'you' ? 'your word' : 'from what came back'}</span>}
          {onOutcome && (
            <label className="outbox-override">
              <span className="muted">Say otherwise:</span>
              <select value="" onChange={(e) => { if (e.target.value) onOutcome(e.target.value as Outcome) }} disabled={busy} aria-label="How it landed">
                <option value="">choose…</option>
                {OUTCOME_CHOICES.map((o) => <option key={o} value={o}>{OUTCOME_WORDS[o]}</option>)}
              </select>
            </label>
          )}
        </p>
      )}
      {row.status !== 'sent' && (
        <p className="muted small">Open it from <Link to="/outreach">Outreach</Link> to finish it.</p>
      )}
    </li>
  )
}

/** A section of the list, with the honest empty line. */
export function OutboxSection({ title, messages, empty, render }: { title: string; messages: OutboundView[]; empty: string; render: (v: OutboundView) => ReactNode }) {
  return (
    <section className="outbox-section">
      <h3>{title} <span className="muted small">{messages.length}</span></h3>
      {messages.length === 0 ? <p className="muted small">{empty}</p> : <ul className="plain outbox-list">{messages.map(render)}</ul>}
    </section>
  )
}

/** Whether a send from the mailbox is possible, in a sentence, and the one control that changes it. */
export function GrantPanel({ grant, onStart, onRevoke, busy = false, notice }: { grant: SendGrantView; onStart: () => void; onRevoke: () => void; busy?: boolean; notice?: string | null }) {
  return (
    <div className="card outbox-grant" id="grant">
      <h3>Sending from your mailbox</h3>
      {notice && <p className="small" role="status">{notice}</p>}
      <p className="small">{grant.why}</p>
      <p className="muted small">By default you copy a message or open it in Outlook and send it yourself. With this on, an approved message goes from your own mailbox when you click Send — one message, one click, never on a schedule.</p>
      <div className="actions">
        {grant.configured && !grant.granted && <button type="button" className="button small primary" onClick={onStart} disabled={busy}>Send from my mailbox</button>}
        {grant.granted && <button type="button" className="button small" onClick={onRevoke} disabled={busy}>Stop sending from {grant.address ?? 'my mailbox'}</button>}
      </div>
    </div>
  )
}
